import {booleanAttribute, Component, Input, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormControlState, FormGroup, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AppState} from "@store/state/app.state";
import {FormStatus} from "@iw-shop/forms/components/submit-button/submit-button.component";
import {ApiContactsSetRequest} from "@api/contacts";
import {ContactService} from "@iw-shop/contact/services/contact.service";
interface LogicBlock {
    operator: 'and' | 'or';
    rules: {
        field: string;
        values: (string | number | boolean)[];
    }[];
}
interface DynamicField {
    label: string,
    required?: boolean,
    disabled?: boolean,
    value?: string,
    hidden?: boolean,
    input?: {
        regex?: string,
    }
    textarea?: {},
    select?: {
        options: string[]
    },
    requiredIf?: LogicBlock[],
    visibleIf?: LogicBlock[]
}

export interface DynamicFormGroup {
    headline?: string,
    field?: DynamicField,
    fieldsInRow?: DynamicField[]

}

@Component({
    selector: 'iw-shop-contact-dynamic',
    standalone: false,
    templateUrl: './contact-dynamic.component.html',
    styleUrls: ['./contact-dynamic.component.scss']
})
export class ContactDynamicComponent implements OnInit {
    @Input() email_subject: string = 'Dynamische Kontaktanfrage';
    @Input() submit_label: string = 'Absenden';
    @Input() headline: string | null = null;
    @Input() fields: DynamicFormGroup[] = [];
    @Input() fields_json?: string;
    @Input({ transform: booleanAttribute })
    require_terms_acceptance: boolean = true;


    form: FormGroup;

    formStatus: FormStatus = null;

    apiError?: {
        notification: string,
        errors?: { key: string, error: string }[]
    };

    captcha: boolean = false;

    constructor(protected fb: FormBuilder, protected store: Store<AppState>, protected contactService: ContactService) {
        this.form = this.fb.group({});
    }

    ngOnInit() {
        this.form.addControl(
            'terms',
            this.fb.control(
                false,
                this.require_terms_acceptance ? [Validators.requiredTrue] : []
            )
        );
        if (this.fields_json) {
            this.fields = JSON.parse(this.fields_json);
        }
        this.fields.map(
            f => {
                if (f.field) {
                    this.setField(f.field)
                }
                if (f.fieldsInRow) {
                    f.fieldsInRow.map(
                        f2 => {
                            if (f2) {
                                this.setField(f2)
                            }
                        }
                    )
                }
            }
        );
        // Bei jeder Änderung im Formular Validierung neu prüfen
        this.form.valueChanges.subscribe(() => {
            this.updateDynamicValidators();
        });
    }

// Hilfsmethode: Prüft ein Array von Logik-Blöcken
// (Alle Blöcke im Array müssen 'true' ergeben)
    checkConditions(logicBlocks: LogicBlock[] | undefined): boolean {
        if (!logicBlocks || !Array.isArray(logicBlocks) || logicBlocks.length === 0) {
            return false;
        }

        const currentFormValues = this.form.getRawValue();

        return logicBlocks.every(block => {
            const checkRule = (rule: any) => {
                const cleanKey = this.getStringWithoutSpecialCharacter(rule.field);
                const val = currentFormValues[cleanKey];

                // Normalisierung: null, undefined oder leerer String werden gleich behandelt
                const normalizedVal = (val === null || val === undefined) ? "" : val;

                return rule.values.includes(normalizedVal);
            };

            if (block.operator === 'or') {
                return block.rules.some(checkRule);
            } else {
                return block.rules.every(checkRule);
            }
        });
    }

    isVisible(field: any): boolean {
        // Da dein JSON { field: { label: ... } } ist, müssen wir prüfen,
        // ob wir das äußere oder innere Objekt haben
        const target = field?.field ? field.field : field;

        if (!target || !target.visibleIf || target.visibleIf.length === 0) {
            return true;
        }
        return this.checkConditions(target.visibleIf);
    }

    isRequired(f: DynamicField): boolean {
        // 1. Unsichtbare Felder sind nie required
        if (!this.isVisible(f)) return false;

        // 2. Statisch required
        if (f.required) return true;

        // 3. Dynamisch required (über deine neue Logik)
        if (f.requiredIf && f.requiredIf.length > 0) {
            return this.checkConditions(f.requiredIf);
        }

        return false;
    }

    updateDynamicValidators() {
        this.fields.forEach(group => {
            // Sammle alle Felder der Gruppe (einzeln oder aus Row)
            const allFields: DynamicField[] = [];
            if (group.field) allFields.push(group.field);
            if (group.fieldsInRow) allFields.push(...group.fieldsInRow);

            allFields.forEach(f => {
                const controlName = this.getStringWithoutSpecialCharacter(f.label);
                const control = this.form.get(controlName);

                if (control) {
                    // Prüfe mit deiner Funktion, ob es gerade required sein MUSS
                    if (this.isRequired(f)) {
                        // Setze den Validator nur, wenn er noch nicht da ist
                        if (!control.hasValidator(Validators.required)) {
                            control.setValidators([Validators.required]);
                            control.updateValueAndValidity({ emitEvent: false });
                        }
                    } else {
                        // Entferne den Validator
                        if (control.hasValidator(Validators.required)) {
                            control.clearValidators();
                            control.updateValueAndValidity({ emitEvent: false });
                        }
                    }
                }
            });
        });
    }

    getStringWithoutSpecialCharacter(value: string): string {
        return value.replace(/[\s\W]/g, '').toLowerCase();
    }

    addControl(label: string, required: boolean, regex?: string, value?: string) {
        let validators = [];
        if (required) {
            validators.push(Validators.required)
        }
        if (regex) {
            validators.push(Validators.pattern(regex))
        }
        this.form.addControl(label, this.fb.control(value, validators));
    }

    getInputType(f: DynamicField): 'input' | 'select' | 'textarea' {
        if (f.select) {
            return 'select';
        }
        if (f.textarea) {
            return 'textarea'
        }
        return 'input';
    }

    setField(f: DynamicField) {
        let validators = [];
        if (this.isRequired(f)) {
            validators.push(Validators.required)
        }
        if (f?.input?.regex) {
            validators.push(Validators.pattern(f?.input?.regex))
        }
        this.form.addControl(
            this.getStringWithoutSpecialCharacter(f?.label ?? ''),
            this.fb.control(
                { value: f?.value ?? '', disabled: f.disabled ?? false }, // Hier wird der Status gesetzt
                validators
            )
        );
    }

    isFormRequestError(value: string): boolean {
        let form = this.form?.get(value);
        return (
            (
                form?.errors ||
                this.isFormRequestApiError(value)
            ) &&
            this.formStatus == 'submitForm');
    }

    isFormRequestApiError(key: string): boolean {
        return !!this.apiError?.errors?.filter(r => r.key == key)[0];
    }

    setCaptcha(value: boolean) {
        setTimeout(() => {
            this.captcha = value;
        }, 100);

    }

    submit() {
        this.formStatus = 'submitForm';
        this.apiError = undefined;

        if (this.captcha && this.form.valid) {
            this.formStatus = 'loading';
            let data: ApiContactsSetRequest['params']['data'] = {
                type: 'contact',
                subject: this.email_subject,
                message: ''
            };

            if ((this.form.get('email')?.value ?? '').length > 0) {
                data.email = this.form.get('email')?.value;
            }

            let message: string = '';

            this.fields.map(
                f => {
                    if (f.field) {
                        let value = this.form.get(this.getStringWithoutSpecialCharacter(f.field.label ?? ''))?.value ?? '';
                        if(f.field?.label == 'E-Mail' && value.length > 0){
                            data.email = this.form.get(this.getStringWithoutSpecialCharacter('E-Mail'))?.value;
                        } else if (value.length > 0) {
                            message = message + '<b>' + f.field.label + '</b><br>' + this.form.get(this.getStringWithoutSpecialCharacter(f.field.label ?? ''))?.value + '<br>';
                        }
                    }
                    if (f.fieldsInRow) {
                        f.fieldsInRow.map(
                            f2 => {
                                if (f2) {
                                    let value = this.form.get(this.getStringWithoutSpecialCharacter(f2.label ?? ''))?.value ?? '';
                                    if (value.length > 0) {
                                        message = message + '<b>' + f2.label + '</b><br>' + this.form.get(this.getStringWithoutSpecialCharacter(f2.label ?? ''))?.value + '<br>';
                                    }
                                }
                            }
                        )
                    }
                }
            );

            data.message = message;

            this.contactService.set(data)
                .subscribe(
                    {
                        next: (r) => {
                            this.formStatus = 'submitForm';
                            if (r.error) {
                                this.apiError = {notification: r.error.message};
                                if (r.error.input) {
                                    this.apiError.errors = r.error.input;
                                }
                                window.scroll({
                                    top: 0,
                                    left: 0,
                                    behavior: 'smooth'
                                });
                            } else {
                                this.formStatus = 'success';
                            }
                        },
                        error: () => {
                            this.formStatus = 'submitForm';
                        }
                    });
        }
    }
}
